import React from 'react';
import PropTypes from 'prop-types';
import HeaderFooterLayout from '../components/home/layouts/HeaderFooterLayout';
import SEOHead from '../components/seo-head';
import { initializeApollo } from '../../apollo-ssr';
import { getServerSideToken } from '../utils/auth-utils';
import { isWebView } from '../utils/helpers';
import fetchHeaderFooterData from '../utils/header-footer-fetcher';
import GET_PAGE_CONTENT_BY_CONTENT_ID from '../gql/queries/get-page-content-by-content-id.gql';
import GET_JSON_VARIABLE from '../gql/queries/get-json-variable.gql';
import PageDataNotFound from '../components/shared/PageDataNotFound';
import HtmlSnippet from '../components/shared/html-snippet';

export default function DefaultHomePage({
  headerData,
  footerData,
  headerHTMLContent,
  footerHTMLContent,
  renderMode,
  homeScreenHtml,
  homeSeoMeta,
  contentId,
}) {
  if (!homeScreenHtml) {
    return <PageDataNotFound noMsg="No Data Available" />;
  }

  const isWebViewAvailable = isWebView();
  if (isWebViewAvailable) {
    return <div dangerouslySetInnerHTML={{ __html: homeScreenHtml }} />;
  }
  return (
    <>
      <SEOHead data={homeSeoMeta} />
      <HtmlSnippet entityId={contentId} entityType="homepage-unified" postLoginAction="requestCallBack" />
      <HeaderFooterLayout
        paddingClass="p-2"
        headerData={headerData}
        footerData={footerData}
        headerHTMLContent={headerHTMLContent}
        footerHTMLContent={footerHTMLContent}
        renderMode={renderMode}
        isHomePage
      >
        <div dangerouslySetInnerHTML={{ __html: homeScreenHtml }} />
      </HeaderFooterLayout>
    </>
  );
}

DefaultHomePage.propTypes = {
  headerData: PropTypes.object,
  footerData: PropTypes.object,
  headerHTMLContent: PropTypes.string,
  footerHTMLContent: PropTypes.string,
  renderMode: PropTypes.object,
  homeScreenHtml: PropTypes.string,
  contentId: PropTypes.string,
  homeSeoMeta: PropTypes.object,
};

export async function getServerSideProps(context) {
  let hostname;
  if (context && context.req && context.req.headers && context.req.headers.host) {
    hostname = context.req.headers.host;
  } else {
    hostname = 'nextias.com';
  }

  const userToken = getServerSideToken(context.req);
  const apolloClient = initializeApollo(null, userToken, hostname);

  if (!userToken) {
    // Anonymous → allow CDN caching
    context.res.setHeader(
      'Cache-Control',
      'public, s-maxage=1800, stale-while-revalidate=60',
    );
  }

  try {
    // Fetch HTML data for SSR
    const homeScreenQueryResponse = await apolloClient.query({
      query: GET_PAGE_CONTENT_BY_CONTENT_ID,
      variables: { contentId: 'homepage-unified' },
      errorPolicy: 'ignore',
    });

    const homeScreenHtml = homeScreenQueryResponse?.data?.getPageContentByContentId?.htmlContent || '';
    const contentId = homeScreenQueryResponse?.data?.getPageContentByContentId?.contentId || '';

    const {
      headerData,
      footerData,
      headerHTMLContent,
      footerHTMLContent,
      renderMode,
    } = await fetchHeaderFooterData(apolloClient, 'en');

    const { data: jsonData } = await apolloClient.query({
      query: GET_JSON_VARIABLE,
      variables: {
        name: 'homeSeoMeta',
        language: 'en',
      },
      errorPolicy: 'ignore',
    });

    const homeSeoMeta = jsonData?.getJsonVariable?.value || {};

    return {
      props: {
        headerData,
        footerData,
        headerHTMLContent,
        footerHTMLContent,
        renderMode,
        homeScreenHtml,
        contentId,
        homeSeoMeta,
        apolloState: apolloClient.cache.extract(),
      },
    };
  } catch (error) {
    console.error('Error fetching header/footer data for home page:', error);
    return {
      props: {
        headerData: null,
        footerData: null,
        headerHTMLContent: null,
        footerHTMLContent: null,
        homeScreenHtml: null,
        homeSeoMeta: {},
        renderMode: { header: 'json', footer: 'json' },
      },
    };
  }
}
